import React from 'react';
import { generatePageDimension } from '../../../../mappers/polotno';
import AnimatedText from './AnimatedText';
import { getBlur, getTextShadow } from '../../../../utils/polotno';
import useLayerAnimation from './hooks/animation';
import { throttle } from 'lodash';

let timeout = null;

function SBLayerAnimatedText({ setting,  portrait, canPlay }) {
  const { text_animation , width, height, x, y, opacity, text, fontUrl } = setting || {};

  const [pageWidth, pageHeight] = React.useMemo(
    () => generatePageDimension(portrait),
    [portrait]
  );

  const relativeX = (x / pageWidth) * 100;
  const relativeY = (y / pageHeight) * 100;
  const relativeWidth = (width / pageWidth) * 100;
  const relativeHeight = (height / pageHeight) * 100;

  const [containerRatio, setContainerRatio] = React.useState(1);
  const containerRef = useLayerAnimation(setting, canPlay, [pageWidth, pageHeight])

  React.useEffect(() => {
    const onContainerResize = throttle(() => {
          if (!containerRef.current) return;
          const currentWidth = containerRef.current.offsetWidth;
          const currentHeight = containerRef.current.offsetHeight;
          const widthRatio = currentWidth / width;
          const heightRatio = currentHeight / height ;
          setContainerRatio(Math.max(widthRatio, heightRatio));
    }, 200);
    onContainerResize();
    //FIXME: this is a workaround for the resize event not firing on the first render
    timeout = setTimeout(() => {
      onContainerResize()
    }, 500);
    window.addEventListener('resize', onContainerResize);
    return () => {
      window.removeEventListener('resize', onContainerResize);
      clearTimeout(timeout);
    };
  }, [containerRef.current, pageWidth, pageHeight, canPlay]);

  const isGradient = (setting.fill || '').includes('linear-gradient');
  return (
    <>
      {setting.backgroundEnabled && (<div className="background"></div>)}
        <div
          className="animated-text"
          ref={containerRef}
          style={{
            position: 'absolute',
            left: `${relativeX}%`,
            top: `${relativeY}%`,
            width: `${relativeWidth}%`,
            height: `${relativeHeight}%`,
            opacity: `${opacity || 1}`
          }}
        >
          {canPlay && <AnimatedText text={text} animation={text_animation} id={setting.id}/>}
        </div>
      <style jsx>{`
        @font-face {
            font-family: ${setting.fontFamily};
            src: url(${fontUrl || ''});
        }
        .animated-text ::global(.letter), .animated-text ::global(.word) {
          display: inline-block;
          background-image: ${isGradient ? setting.fill : 'none'};
          -webkit-background-clip: ${isGradient ? 'text' : 'none'};
          color: ${isGradient ? 'transparent' : 'none'};
          background-size: 100% 100%;
          background-repeat: no-repeat;
          background-clip: text;
          background-attachment: local;
        }
        .animated-text ::global(p) {
          margin: 0;
          padding: 0;
        }
        .background {
          position: absolute;
          left: ${relativeX}%;
          top: ${relativeY}%;
          width: ${relativeWidth}%;
          height: ${relativeHeight}%;
          opacity: ${setting.backgroundOpacity || 1};
          border-radius: ${setting.backgroundCornerRadius * 100}px;
          background-color: ${setting.backgroundColor};
          color: ${setting.backgroundColor};
          border: ${setting.backgroundPadding * 100}px solid currentColor;
          box-sizing: content-box;
          transform: translate(-${setting.backgroundPadding * 100}px, -${setting.backgroundPadding * 100}px);
          transform-origin: center center;
        }
        .animated-text {
          -webkit-font-smoothing: antialiased;
          font-size: ${setting.fontSize * containerRatio}px;
          font-family: ${setting.fontFamily};
          vertical-align: ${setting.verticalAlign};
          font-weight: ${setting.fontWeight};
          font-style: ${setting.fontStyle};
          text-align: ${setting.align};
          text-shadow: ${getTextShadow(setting)};
          filter: blur(${getBlur(setting)});
          transform: rotate(${setting.rotation}deg);
          transform-origin: ${setting.rotation !== 0 ? 'left top' : 'center' };
          letter-spacing: ${setting.letterSpacing * containerRatio}rem;
          background-image: ${isGradient ? setting.fill : 'none'};
          -webkit-background-clip: ${isGradient ? 'text' : 'none'};
          color: ${isGradient ? 'transparent' : setting.fill};
          line-height: ${setting.lineHeight};
          display: inline-block;
          -webkit-text-stroke-width: ${setting.strokeWidth / 2}px;
          -webkit-text-stroke-color: ${setting.stroke};
          word-spacing: normal;
          }
      `}</style>
    </>
  );
}

export default SBLayerAnimatedText;
